import React, { type ChangeEvent, type PropsWithChildren, useState }  from 'react';
import { useForm } from 'react-hook-form';
import Upload, { type UploadProps } from 'rc-upload';

interface Props extends PropsWithChildren {
  productOption: Array<object>;
  new_support_service_ticket__c: number;
  password: string;
  onSubmit: (data: any) => void;
};

const ReactForm = ({ productOption, new_support_service_ticket__c, password, onSubmit }: Props) => {
  const { register, handleSubmit, formState: { isSubmitting, errors }, setValue, setError, clearErrors } = useForm();
  //default state
  const [categoryOption, setCategoryOption] = useState<Array<object>>([]);
  const [moduleOption, setModuleOption] = useState<Array<object>>([]);
  const [versionOption, setVersionOption] = useState<Array<object>>([]);
  const [showCategory, setShowCategory] = useState<boolean>(false);
  const [showModule, setShowModule] = useState<boolean>(false);
  const [showVersion, setShowVersion] = useState<boolean>(false);
  const [issueArray, setIssueArray] = useState<Array<string>>([]);
  const [severity, setSeverity] = useState<number>(0);
  const [noSupportArray, setNoSupportArray] = useState<Array<string>>([]);
  const [upload_system , setUploadSystem] = useState<string>('');
  const [fileList, setFileList] = useState<Array<string>>([]);
  const [fileSasUlrs, setFileSasUlrs] = useState<Array<string>>([]);
  const [maxFiles, setMaxFiles] = useState<number>(0);
  const [uploading, setUploading] = useState(false);

  const getCategories = async (e: ChangeEvent<HTMLSelectElement>) => {
    const selectedIndex = e.target.selectedIndex;
    const productID = e.target.options[selectedIndex].dataset.pid;
    try {
      const response = await fetch(`https://www.avepoint.com/api/v1/support-products/${productID}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded"
        },
        redirect: "follow"
      });
      const result = await response.json();
      if (response.ok) {
        // Clear errors and update states
        clearErrors(["new_products", "new_casecategory", "new_modules", "new_docavesection"]);
        setCategoryOption(result.data);
        setShowCategory(true);
        setShowModule(false);
        setShowVersion(false);
      } else {
        throw new Error(`Error: ${result.message}`);
      }
    } catch (error) {
      throw new Error(`Error: ${error}`);
    }
  };
  
  const getModulesAndVersions = async (e: ChangeEvent<HTMLSelectElement>) => {
    const selectedIndex = e.target.selectedIndex;
    const categoryID = e.target.options[selectedIndex].dataset.cid;
    try {
      const response = await fetch(`https://www.avepoint.com/api/v1/support-categories/${categoryID}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded"
        },
        redirect: "follow"
      });
      const result = await response.json();
      if (response.ok) {
        // Clear errors and set module options
        clearErrors(["new_casecategory", "new_modules", "new_docavesection"]);
        setModuleOption(result.data);
        if (result.data.length === 1) {
          setValue('new_modules', result.data[0].value);
          setShowVersion(false);
          await getVersions(undefined, categoryID);
        } else {
          setShowModule(true);
          setShowVersion(false);
        }
      } else {
        throw new Error(`Error fetching modules: ${result.message}`);
      }
    } catch (error) {
      throw new Error(`Fetch error: ${error}`);
    }
  };
  
  const getVersions = async (e?: ChangeEvent<HTMLSelectElement>, cid?: string) => {
    const categoryID = cid ?? e?.target?.options[e.target.selectedIndex].dataset.cid;
    if (!categoryID) {
      return;
    }
    try {
      const urlencoded = new URLSearchParams();
      urlencoded.append("category_id", categoryID);
      const response = await fetch("https://www.avepoint.com/api/v1/support-categories/", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded"
        },
        body: urlencoded,
        redirect: "follow"
      });
      const result = await response.json();
      if (response.ok) {
        clearErrors(["new_modules", "new_docavesection"]);
        setVersionOption(result.data);
        if (result.data.length === 1) {
          setValue('new_docavesection', result.data[0].version);
        } else {
          setShowVersion(true);
        }
      } else {
        throw new Error(`Error fetching versions: ${result.message}`);
      }
    } catch (error) {
      throw new Error(`Fetch error: ${error}`);
    }
  };  

  const updateIssueAndSeverity = (e: ChangeEvent<HTMLInputElement>) => {
    const issueTitle = e.target.title;
    const issueValue = Number(e.target.value);
    setIssueArray((prevIssueArray) => {
      let newIssueArray;
      let newSeverity;
      if (e.target.checked) {
        newIssueArray = [...prevIssueArray, issueTitle];
        newSeverity = severity + issueValue;
      } else {
        newIssueArray = prevIssueArray.filter(issue => issue !== issueTitle);
        newSeverity = Math.max(0, severity - issueValue);
      }
      setSeverity(newSeverity);
      setValue("new_severity__c", newSeverity);
      setValue("issuetext", newIssueArray.join());
      return newIssueArray;
    });
  };

  const updateSupportArray = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setNoSupportArray((prevNoSupportArray) => {
      let newNoSupportArray;
      if (e.target.checked) {
        newNoSupportArray = [...prevNoSupportArray, value];
      } else {
        newNoSupportArray = prevNoSupportArray.filter(noSupport => noSupport !== value);
      }
      setValue("no_support_arr", newNoSupportArray.join());
      return newNoSupportArray;
    });
  };
  
  const multiEmails = (v: string): boolean => {
    const emails = v ? v.split(/[,;]+/).map(email => email.trim()) : [];
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emails.every(email => regex.test(email));
  };
  
  const showUpload = (e: ChangeEvent<HTMLInputElement>) => {
    const uploadType = e.target.value;
    // Clear or set error based on the selected upload type
    if (uploadType === "ftp") {
      clearErrors("file");
    } else {
      setError("file", {
        type: "manual",
        message: "ファイルのアップロードをお願いします!" // Update this message as needed
      });
    }
    setUploadSystem(uploadType);
  };
  
  // Generic form data builder function
  const buildFormData = (folder: string, file?: File, fileName?: string) => {
    const formData = new FormData();
    formData.append('folder', folder);
    if (file) formData.append('file', file);
    if (fileName) formData.append('fileName', fileName);
    return formData;
  };

  // Generic fetch request function
  const fetchRequest = async (url: string, formData: FormData, onSuccess: Function, onError: Function) => {
    try {
      const response = await fetch(url, {
        method: 'POST',
        body: formData,
      });
      if (response.ok) {
        onSuccess(response);
      } else {
        const errorMessage = await response.text();
        onError(new Error(errorMessage));
      }
    } catch (error) {
      onError(error);
    }
  };

  const customRequest = ({ action, file, onProgress, onSuccess, onError }: any) => {
    const formData = buildFormData(new_support_service_ticket__c as unknown as string, file);
    if (onProgress) {
      onProgress({ percent: 50 });
    }
    fetchRequest(action, formData,
      async (response: Response) => {
        if (onProgress) onProgress({ percent: 100 });
        const data = await response.json();
        onSuccess(data, file);
      },
      onError
    );
    return {
      abort() {
        throw new Error('Upload aborted');
      },
    };
  };

  // Delete file function
  const deleteFile = async (fileName: any) => {
    const formData = buildFormData(new_support_service_ticket__c as unknown as string, undefined, fileName);
    await fetchRequest('/api/files/delete', formData, 
      () => {
        let newMaxFiles = maxFiles - 1;
        setFileList((prevFileList) => prevFileList.filter(file => file !== fileName));
        setFileSasUlrs((prevUrl) => prevUrl.filter(url => !new RegExp(fileName, "i").test(decodeURIComponent(url))));
        setMaxFiles(Math.max(0, newMaxFiles));
        if(newMaxFiles <= 0){
          setError("file", {
            type: "manual",
            message: "ファイルのアップロードをお願いします!"
          });
        }
        // update form values
        setValue('sasUrls', fileSasUlrs.filter(url => !new RegExp(fileName, "i").test(decodeURIComponent(url))));
      },
      (error: Error) => {
        throw new Error(`Error deleting file ${error.message}`);
      }
    );
  };

  // Upload configuration item
  const uploadProps: UploadProps = {
    action: '/api/files/upload',
    type: 'drag',
    multiple: false,  // Single file upload
    onStart() {
      setUploading(true);
    },
    onProgress({ percent }) {
      //console.log('onProgress', `${percent}%`);
    },
    onSuccess(data: any, file: any) {
      let newMaxFiles = maxFiles + 1;
      setUploading(false);
      setFileList((prevList) => [...prevList, file.name]);
      setFileSasUlrs((prevUrl) => [...prevUrl, data.sasUrl]);
      setMaxFiles(newMaxFiles);
      // remove error message
      clearErrors("file");
      // update form values
      setValue('file', file.name);
      setValue('sasUrls', [...fileSasUlrs, data.sasUrl]);
    },
    onError(error: any) {
      setError("file", {
        type: "manual",
        message: "ファイルのアップロードをお願いします!"
      });
      setUploading(false);
    },
    beforeUpload(file: any) {
      const acceptFileTypes = /\.(gif|jpe?g|png|docx?|pdf|xlsx?|txt|log|zip(\.\d+)?|7z(\.\d+)?|rar(\.\d+)?|pptx?)$/i;
      const maxFileSize = 100 * 1024 * 1024; // 100MB
      return new Promise<void>((resolve, reject) => {
        // Check file type and size
        const isValidFile = acceptFileTypes.test(file.name) && file.size <= maxFileSize;
        setTimeout(() => {
          if (isValidFile) {
            clearErrors("file"); // Clear errors if the file is valid
            resolve();
          } else {
            // Set error before rejecting
            setError("file", {
              type: "manual",
              message: "ファイルタイプとファイルサイズのチェックをお願いします!"
            });
            reject();
          }
        }, 1000);
      });
    },
    customRequest: customRequest,
    style: { display: 'inline-block', width: 20, height: 20, background: '#eee' }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className='w-full'>
      <div className='mx-auto mt-10 flex flex-row gap-6'>
        <div className='w-1/3'>
          <h2>お客様情報</h2>
        </div>
        <div className='w-2/3'>
          <div className='mx-auto'>
            <p className='block text-sm font-bold mb-2'><span className="text-primary">*</span><label htmlFor="fullname">氏名</label></p>
            <input className='px-3 py-2 bg-white border-gray-200 focus:border-gray-300 focus:outline-none w-full rounded-md sm:text-sm' type="text" {...register('fullname', { required: '必須フィールド' })}/>
            {errors.fullname?.message && <p className="text-primary">{errors.fullname.message as string}</p>}
          </div>
          <div className='mx-auto mt-6'>
            <p className='block text-sm font-bold mb-2'><span className="text-primary">*</span><label htmlFor="customerid">会社名</label></p>
            <input className='px-3 py-2 bg-white border-gray-200 focus:border-gray-300 focus:outline-none w-full rounded-md sm:text-sm' type="text" {...register('customerid', { required: '必須フィールド' })}/>
            {errors.customerid?.message && <p className="text-primary">{errors.customerid.message as string}</p>}
          </div>
          <div className='mx-auto mt-6'>
            <p className='block text-sm font-bold mb-2'>エンドのお客様名</p>
            <input className='px-3 py-2 bg-white border-gray-200 focus:border-gray-300 focus:outline-none w-full rounded-md sm:text-sm' type="text" {...register('enduser')}/>
          </div>
          <div className='mx-auto mt-6'>
            <p className='block text-sm font-bold mb-2'><span className="text-primary">*</span><label htmlFor="new_suppliedemail">担当者様のメール アドレス</label></p>
            <input className='px-3 py-2 bg-white border-gray-200 focus:border-gray-300 focus:outline-none w-full rounded-md sm:text-sm' type="text" {...register('new_suppliedemail', {validate: (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)})}/>
            {errors.new_suppliedemail && <p className="text-primary">必須フィールド</p>}
          </div>
          <div className='mx-auto mt-6'>
            <p className='block text-sm font-bold mb-2'>担当者様以外の方のメール アドレス (複数のメール アドレスを追加したい場合は、"," または ";" で区切ります)</p>
            <input className='px-3 py-2 bg-white border-gray-200 focus:border-gray-300 focus:outline-none w-full rounded-md sm:text-sm' type="text" {...register('addresses', { validate: (value: string) => multiEmails(value)} )}/>
            {errors.addresses && <p className="text-primary">有効なメールアドレスを入力してください。</p>}
          </div>
          <div className='mx-auto mt-6'>
            <p className='block text-sm font-bold mb-2'><span className="text-primary">*</span><label htmlFor="new_suppliedphone">電話番号</label></p>
            <input className='px-3 py-2 bg-white border-gray-200 focus:border-gray-300 focus:outline-none w-full rounded-md sm:text-sm' type="text" {...register('new_suppliedphone', { required: '必須フィールド' })}/>
            {errors.new_suppliedphone?.message && <p className="text-primary">{errors.new_suppliedphone.message as string}</p>}
          </div>
          <div className='mx-auto mt-6'>
            <p className='block text-sm font-bold mb-2'><span className="text-primary">*</span><label htmlFor="new_preferred_method__c">ご連絡方法</label></p>
            <input type="radio" value="Phone" className="border-gray-200 appearance-none focus:ring-0 focus:ring-white checked:bg-white checked:text-black checked:border-green checked:bg-[url('https://assets.avepoint.com/upload/icons/form-circle.svg')] checked:bg-[length:9px_9px] rounded-full w-5 h-5" {...register('new_preferred_method__c', { required: '必須フィールド' })}/><span className="ml-2 mr-5">電話</span>
            <input type="radio" value="Email" className="border-gray-200 appearance-none focus:ring-0 focus:ring-white checked:bg-white checked:text-black checked:border-green checked:bg-[url('https://assets.avepoint.com/upload/icons/form-circle.svg')] checked:bg-[length:9px_9px] rounded-full w-5 h-5" {...register('new_preferred_method__c', { required: '必須フィールド' })}/><span className="ml-2">メール</span>
            {errors.new_preferred_method__c?.message && <p className="text-primary">{errors.new_preferred_method__c.message as string}</p>}
          </div>
        </div>
      </div>
      <div className='mx-auto mt-10 flex flex-row gap-6'>
        <div className='w-1/3'>
          <h2>製品についての情報</h2>
        </div>
        <div className='w-2/3'>
          <div>
            <p className='block text-sm font-bold mb-2'><span className="text-primary">*</span>製品</p>
            <select className='block w-full px-4 py-3 mb-3 leading-tight bg-white border border-gray-200 rounded appearance-none focus:outline-none focus:bg-white focus:border-gray-500' defaultValue="" {...register('new_products', { required: '必須フィールド' })} onChange={e => getCategories(e)}>
              <option value="" disabled>選択</option>
              {productOption?.map(({ id, name, name_jp, value }: Record<string, any>) => (
                <option key={name+id} value={value} data-pid={id}>{name_jp}</option>
              ))}
            </select>
            {errors.new_products?.message && <p className="text-primary">{errors.new_products.message as string}</p>}
          </div>
          <div className={`mt-6 ${showCategory ? "" : "none"}`}>
            <p className='block text-sm font-bold mb-2'><span className="text-primary">*</span>カテゴリ</p>
            <select className='block w-full px-4 py-3 mb-3 leading-tight bg-white border border-gray-200 rounded appearance-none focus:outline-none focus:bg-white focus:border-gray-500' defaultValue="" {...register('new_casecategory', { required: '必須フィールド' })} onChange={e => getModulesAndVersions(e)}>
              <option value="" disabled>選択</option>
              {categoryOption?.map(({ id, name, name_jp, value, product_id }: Record<string, any>) => (
                <option key={name+id} value={value} data-cid={id} data-pid={product_id}>{name_jp}</option>
              ))}
            </select>
            {errors.new_casecategory?.message && <p className="text-primary">{errors.new_casecategory.message as string}</p>}
          </div>
          <div className={`mt-10 ${showModule ? "" : "none"}`}>
            <p className='block text-sm font-bold mb-2'><span className="text-primary">*</span>モジュール</p>
            <select className='block w-full px-4 py-3 mb-3 leading-tight bg-white border border-gray-200 rounded appearance-none focus:outline-none focus:bg-white focus:border-gray-500' defaultValue="" {...register('new_modules', { required: '必須フィールド' })} onChange={e=> getVersions(e)}>
              <option value="" disabled>選択</option>
              {moduleOption?.map(({ id, name, name_jp, value, category_id }: Record<string, any>) => (
                <option key={name+id} value={value} data-cid={category_id}>{name_jp}</option>
              ))}
            </select>
            {errors.new_modules?.message && <p className="text-primary">{errors.new_modules.message as string}</p>}
          </div>
          <div className={`mt-10 ${showVersion ? "" : "none"}`}>
            <p className='block text-sm font-bold mb-2'><span className="text-primary">*</span>バージョン</p>
            <select className='block w-full px-4 py-3 mb-3 leading-tight bg-white border border-gray-200 rounded appearance-none focus:outline-none focus:bg-white focus:border-gray-500' defaultValue="" {...register('new_docavesection', { required: '必須フィールド' })}>
              <option value="" disabled>選択</option>
              {versionOption?.map(({ id, name, version, product_id }: Record<string, any>) => (
                <option key={name+id} value={name} data-pid={product_id}>{version}</option>
              ))}
            </select>
            {errors.new_docavesection?.message && <p className="text-primary">{errors.new_docavesection.message as string}</p>}
          </div>
          <div className='mt-6'>
            <p className='block text-sm font-bold mb-2'><span className="text-primary">*</span>SharePoint バージョン</p>
            <select className='block w-full px-4 py-3 mb-3 leading-tight bg-white border border-gray-200 rounded appearance-none focus:outline-none focus:bg-white focus:border-gray-500' defaultValue="" {...register('new_sharepoint_version__c', { required: '必須フィールド' })}>
              <option value="" disabled>選択</option>
              <option value="Not Applicable">Not Applicable</option>
              <option value="WSS 3.0">WSS 3.0</option>
              <option value="WSS 2.0">WSS 2.0</option>
              <option value="Office 365">Office 365</option>
              <option value="SharePoint 2019">SharePoint 2019</option>
              <option value="SharePoint 2016">SharePoint 2016</option>
              <option value="SharePoint 2013">SharePoint 2013</option>
              <option value="SharePoint 2010">SharePoint 2010</option>
              <option value="SharePoint 2007">SharePoint 2007</option>
              <option value="SharePoint 2003">SharePoint 2003</option>
              <option value="SharePoint Foundation 2010">SharePoint Foundation 2010</option>
            </select>
            {errors.new_sharepoint_version__c?.message && <p className="text-primary">{errors.new_sharepoint_version__c.message as string}</p>}
          </div>
        </div>
      </div>
      <div className='mx-auto mt-10 flex flex-row gap-6'>
        <div className='w-1/3'>
          <h2>お問い合わせの詳細</h2>
        </div>
        <div className='w-2/3'>
          <div className='mx-auto'>
            <p className='block text-sm font-bold mb-2'>現在の状況に当てはまるものを以下から選択してください。</p>
            <label className="flex items-center">
              <input type="checkbox" className="border-gray-200 appearance-none focus:ring-0 focus:ring-white checked:bg-white checked:text-black checked:border-green checked:bg-[url('https://assets.avepoint.com/upload/icons/form-check.svg')] checked:bg-[length:12px_12px] rounded w-5 h-5" value={6} title="go-down-or-inaccessible" onChange={e => updateIssueAndSeverity(e)}/>
              <span className="ml-2">この問題により、本番環境が非アクティブ状態またはアクセス不可となっています。</span>
            </label>
            <label className="flex items-center mt-2">
              <input type="checkbox" className="border-gray-200 appearance-none focus:ring-0 focus:ring-white checked:bg-white checked:text-black checked:border-green checked:bg-[url('https://assets.avepoint.com/upload/icons/form-check.svg')] checked:bg-[length:12px_12px] rounded w-5 h-5" value={2} title="preventing-or-forward" onChange={e => updateIssueAndSeverity(e)}/>
              <span className="ml-2">この問題により、プロジェクトの完了または進行がストップしています。</span>
            </label>
            <label className="flex items-center mt-2">
              <input type="checkbox" className="border-gray-200 appearance-none focus:ring-0 focus:ring-white checked:bg-white checked:text-black checked:border-green checked:bg-[url('https://assets.avepoint.com/upload/icons/form-check.svg')] checked:bg-[length:12px_12px] rounded w-5 h-5" value={1} title="affecting-users" onChange={e => updateIssueAndSeverity(e)}/>
              <span className="ml-2">この問題により、複数のユーザーが影響を受けています。</span>
            </label>
            <label className="flex items-center mt-2">
              <input type="checkbox" className="border-gray-200 appearance-none focus:ring-0 focus:ring-white checked:bg-white checked:text-black checked:border-green checked:bg-[url('https://assets.avepoint.com/upload/icons/form-check.svg')] checked:bg-[length:12px_12px] rounded w-5 h-5" value={1} title="reproduced-or-occurred" onChange={e => updateIssueAndSeverity(e)}/>
              <span className="ml-2">この問題は複数回発生しているか、再現されています。</span>
            </label>
            <input type="hidden" value={issueArray.join()} {...register('issuetext')}/>
            <input type="hidden" value={severity} {...register('new_severity__c')}/>
          </div>
          <div className='mx-auto mt-6'>
            <p className='block text-sm font-bold mb-2'>サポートと共有できないアイテムを選択してください。</p>
            <label className="flex items-center">
              <input type="checkbox" className="border-gray-200 appearance-none focus:ring-0 focus:ring-white checked:bg-white checked:text-black checked:border-green checked:bg-[url('https://assets.avepoint.com/upload/icons/form-check.svg')] checked:bg-[length:12px_12px] rounded w-5 h-5" value="avepointlogs" onChange={e => updateSupportArray(e)}/>
              <span className="ml-2">AvePoint ログ</span>
            </label>
            <label className="flex items-center mt-2">
              <input type="checkbox" className="border-gray-200 appearance-none focus:ring-0 focus:ring-white checked:bg-white checked:text-black checked:border-green checked:bg-[url('https://assets.avepoint.com/upload/icons/form-check.svg')] checked:bg-[length:12px_12px] rounded w-5 h-5" value="microsoftlogs" onChange={e => updateSupportArray(e)}/>
              <span className="ml-2">Microsoft ログ</span>
            </label>
            <label className="flex items-center mt-2">
              <input type="checkbox" className="border-gray-200 appearance-none focus:ring-0 focus:ring-white checked:bg-white checked:text-black checked:border-green checked:bg-[url('https://assets.avepoint.com/upload/icons/form-check.svg')] checked:bg-[length:12px_12px] rounded w-5 h-5" value="screenshots" onChange={e => updateSupportArray(e)}/>
              <span className="ml-2">スクリーンショット</span>
            </label>
            <label className="flex items-center mt-2">
              <input type="checkbox" className="border-gray-200 appearance-none focus:ring-0 focus:ring-white checked:bg-white checked:text-black checked:border-green checked:bg-[url('https://assets.avepoint.com/upload/icons/form-check.svg')] checked:bg-[length:12px_12px] rounded w-5 h-5" value="screensharingsessions" onChange={e => updateSupportArray(e)}/>
              <span className="ml-2">画面共有セッション</span>
            </label>
            <input type="hidden" value={noSupportArray.join()} {...register('no_support_arr')} />
          </div>
          <div className='mx-auto mt-6'>
            <p className='block text-sm font-bold mb-2'><span className="text-primary">*</span>お問い合わせの詳細をできるだけ、詳しく記入してください。</p>
            <textarea className='w-full px-3 py-2 border rounded-md border-gray-200 focus:border-gray-300 focus:outline-none placeholder-gray-400 text-base' {...register('description', { required: '必須フィールド' })}></textarea>
            {errors.description?.message && <p className="text-primary">{errors.description.message as string}</p>}
          </div>
        </div>
      </div>
      <div className='mx-auto mt-10 flex flex-row gap-6'>
        <div className='w-1/3'>
          <h2>ログのアップロード</h2>
        </div>
        <div className='w-2/3'>
          <input type="hidden" value={new_support_service_ticket__c} {...register('new_support_service_ticket__c')}/>
          <input type="hidden" value={password} {...register('ftp_password')}/>
          <div className='mx-auto'>
            <p className='block text-sm font-bold mb-2'>転送方法:</p>
            <input type="radio" className="border-gray-200 appearance-none focus:ring-0 focus:ring-white checked:bg-white checked:text-black checked:border-green checked:bg-[url('https://assets.avepoint.com/upload/icons/form-circle.svg')] checked:bg-[length:9px_9px] rounded-full w-5 h-5" {...register('upload_system')} value="s3" onChange={e => showUpload(e)}/><span className="ml-2 mr-5">HTTP</span>
            <input type="radio" className="border-gray-200 appearance-none focus:ring-0 focus:ring-white checked:bg-white checked:text-black checked:border-green checked:bg-[url('https://assets.avepoint.com/upload/icons/form-circle.svg')] checked:bg-[length:9px_9px] rounded-full w-5 h-5" {...register('upload_system')} value="ftp" onChange={e => showUpload(e)}/><span className="ml-2">FTP</span>
          </div>
          <div className={`mx-auto mt-5 ${upload_system == "s3" ? "" : "none"}`}>
            <p className='mb-2'>50 MB 以内のファイルの場合、HTTP プロトコルを使用することができます。</p>
            <p className='mb-2'>このオプションを選択すると、添付ファイルをアップロードしてサポート チームに直接に送信します。</p>
            <p><span className="text-primary">*</span>添付ファイル:</p>
            {/* Upload */}
            <div className='[&_span]:!w-full [&_span]:!h-auto [&_span]:p-8 [&_span]:my-4 [&_span]:rounded-md'>
              <div>
                {maxFiles < 5 && <>
                  <Upload {...uploadProps}>
                  <p className='text-center'>Drag Files Here</p>
                  <p className='text-center my-3'>or</p>
                  <button type="button" className="block py-2 px-4 border-gray-200 mx-auto bg-white rounded-md">Choose Files</button>
                  </Upload>
                {uploading && <p><img className="mx-auto" src="https://assets.avepoint.com/upload/images/icons/loading.gif" alt="uploading" width={40} height={40} loading="eager"/></p>}
                {errors.file && <p className="text-primary pt-4">{errors.file.message ? errors.file.message as string : "ファイルのアップロードをお願いします!"}</p>}</>}
                {!!fileList && <ul className='space-y-3'>
                  {fileList.map((file, index) => (
                    <li className="flex flex-row items-center" key={file + index}>{file}  <button className="ml-6" type="button" onClick={() => deleteFile(file)}><img className="logo" src="https://assets.avepoint.com/upload/images/icons/icons8-close.svg" alt="close" width={20} height={20} loading="eager"/></button></li>
                  ))}
                </ul>}
              </div>
            </div>
            <p className='pt-4'>ログ / スクリーンショットを 50 MB 以下のファイル 1 件に圧縮してください。</p>
          </div>
          <div className={`mx-auto mt-5 ${upload_system == "ftp" ? "" : "none"}`}>
            <p className='mb-2'>50 MB 以上のファイルの場合、FTP プロトコルの使用が推奨されます。</p>
            <p>このオプションを選択すると、FTP 資格情報を担当者様のメールアドレスに直接に送信します。これらの資格情報を使用し、添付ファイルをセキュア FTP サーバーにアップロードしてください。</p>
          </div>
        </div>
      </div>
      <div className='mx-auto mt-10 flex flex-row gap-6'>
        <div className='w-1/3'>
        </div>
        <div className='w-2/3'>
          <p className='block text-sm font-bold mb-2'>AvePoint は、お客様のプライバシーを重視しており、弊社がお客様の情報をどのように扱うかを知っていただくことを重要と考えています。<a href="https://www.avepoint.com/jp/company/terms-and-conditions">ご利用条件</a>、<a href="https://www.avepoint.com/jp/company/privacy-policy">プライバシー ポリシー</a>、および<a href="https://www.avepoint.com/jp/company/privacy-policy-new">個人情報法保護方針</a>をご確認の上、個人情報取扱い同意書に同意していただく必要があります。</p>
          <label className="flex items-center"><input type="checkbox" className="border-gray-200 appearance-none focus:ring-0 focus:ring-white checked:bg-white checked:text-black checked:border-green checked:bg-[url('https://assets.avepoint.com/upload/icons/form-check.svg')] checked:bg-[length:12px_12px] rounded w-5 h-5" {...register('new_gdprconsent', { required: '必須フィールド' })} value="yes"/><span className="ml-2">利用条件、プライバシー ポリシー、個人情報保護方針を確認の上、個人情報取扱い同意書に同意します。</span></label>
          {errors.new_gdprconsent?.message && <p className="text-primary">{errors.new_gdprconsent.message as string}</p>}
        </div>
      </div>
      <div className='mx-auto mt-10 flex flex-row gap-6'>
      <div className='w-1/3'>
      </div>
        <div className='w-2/3'>
          <button className={`survey-button text-white rounded-full h-[50px] w-44 text-center bg-primary font-semibold`} disabled={isSubmitting} type="submit">{isSubmitting ? "送信中..." : "送信"}</button>
        </div>
      </div>
    </form>
  );
};

export default ReactForm;